// The beauty sub-niches, in the Figma grid order. Must match NICHES in
// jubilee-v2-api (dropshipping/onboarding.py).
export const ONBOARDING_NICHES = [
  "lipsticks_and_lip_gloss",
  "eyeshadow_and_eyes",
  "face_and_foundation",
  "skincare",
  "serums_and_face_oils",
  "hair_care",
  "bath_and_body",
  "nail_care",
  "brushes_and_tools",
  "fragrance",
  "mens_grooming",
  "gift_sets",
] as const;

export type OnboardingNiche = (typeof ONBOARDING_NICHES)[number];

// Must match MARKET_SHIPS_TO in jubilee-v2-api.
export const ONBOARDING_MARKETS = [
  "US",
  "EU",
  "UK",
  "CA",
  "AU",
  "WORLDWIDE",
] as const;

export type OnboardingMarket = (typeof ONBOARDING_MARKETS)[number];

// Delivery time and catalogue size shown for each market (from the Figma).
export const MARKET_DETAILS: Record<
  OnboardingMarket,
  { deliveryDays: string; productCount: string }
> = {
  US: { deliveryDays: "1–3", productCount: "1.2M" },
  EU: { deliveryDays: "2–5", productCount: "640k" },
  UK: { deliveryDays: "2–4", productCount: "210k" },
  CA: { deliveryDays: "3–6", productCount: "180k" },
  AU: { deliveryDays: "4–8", productCount: "95k" },
  WORLDWIDE: { deliveryDays: "5–10", productCount: "5M+" },
};

// Products per category, shown on each category tile.
export const CURATED_PRODUCTS_PER_NICHE = 50;

// Product cards shown before the "+N more" tile on the review step.
export const PREVIEW_PRODUCT_COUNT = 5;

export const TASK_POLL_INTERVAL_MS = 1500;
// Give the 35 imports three minutes.
export const MAX_TASK_POLLS = 120;

// Set when the merchant chose "Import Products" and cleared once the import
// ran. It survives the checkout page and Stripe's card redirect.
export const IMPORT_AFTER_PAYMENT_KEY = "onboardingImportAfterPayment";

export type OnboardingProduct = {
  id: string;
  title: string;
  // What the merchant pays and the suggested retail price, as the product
  // cards across the app show them ("You pay" / "You sell").
  formatted_price: string;
  formatted_msrp: string;
  image_cover_url?: string;
  best_selling?: boolean;
  trending?: boolean;
  shipping_time?: number[] | string | number | null;
};

export type OnboardingTaskStatus = {
  status: "queued" | "in_progress" | "completed" | "failed";
  progress: number;
  total_items: number;
  processed_items: number;
  error_message: string | null;
  result_data: {
    imported_count: number;
    failed_product_ids: string[];
    // Picks past the plan's product limit (Starter allows 25), not added.
    over_limit_count?: number;
  } | null;
};

export const toggleNiche = (
  selected: OnboardingNiche[],
  niche: OnboardingNiche
): OnboardingNiche[] =>
  selected.includes(niche)
    ? selected.filter((item) => item !== niche)
    : [...selected, niche];

export const getProductImage = (product: OnboardingProduct) =>
  product.image_cover_url || "";

const parseAmount = (formatted: string) =>
  parseFloat(formatted.replace(/,/g, "").replace(/[^0-9.-]+/g, ""));

const currencySymbol = (formatted: string) =>
  formatted.match(/^[^\d]+/)?.[0].trim() ?? "";

const formatAmount = (symbol: string, amount: number) =>
  `${symbol}${new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount)}`;

export type ProductPricing = {
  sell: string;
  profit: string;
  profitAmount: number;
  symbol: string;
};

export const getProductPricing = (
  product: OnboardingProduct
): ProductPricing | null => {
  const costAmount = parseAmount(product.formatted_price ?? "");
  const sellAmount = parseAmount(product.formatted_msrp ?? "");

  if (!Number.isFinite(costAmount) || !Number.isFinite(sellAmount)) {
    return null;
  }

  const symbol = currencySymbol(product.formatted_msrp);
  const profitAmount = sellAmount - costAmount;

  return {
    sell: formatAmount(symbol, sellAmount),
    profit: formatAmount(symbol, profitAmount),
    profitAmount,
    symbol,
  };
};

// Average profit per sale, or null when there's nothing to average or the
// products are priced in different currencies.
export const getAverageProfit = (products: OnboardingProduct[]) => {
  const pricings = products
    .map(getProductPricing)
    .filter((pricing): pricing is ProductPricing => pricing !== null);

  if (pricings.length === 0) return null;

  const symbols = new Set(pricings.map((pricing) => pricing.symbol));
  if (symbols.size > 1) return null;

  const total = pricings.reduce(
    (sum, pricing) => sum + pricing.profitAmount,
    0
  );
  return formatAmount(pricings[0].symbol, total / pricings.length);
};

export const isTaskFinished = (status: OnboardingTaskStatus["status"]) =>
  status === "completed" || status === "failed";

// Every day count in the product's delivery time ([2, 3], "2-3" or 2).
const shippingDayValues = (product: OnboardingProduct): number[] => {
  const time = product.shipping_time;
  const values = Array.isArray(time)
    ? time
    : typeof time === "string"
      ? time.split("-").map((part) => parseInt(part.trim(), 10))
      : typeof time === "number"
        ? [time]
        : [];
  return values.filter(
    (day) => typeof day === "number" && Number.isFinite(day)
  );
};

const formatDayRange = (days: number[]): string | null => {
  if (days.length === 0) return null;
  const [min, max] = [Math.min(...days), Math.max(...days)];
  return min === max ? String(min) : `${min}–${max}`;
};

// "2–3" from the product's delivery time, or null when it's missing.
export const getShippingDays = (product: OnboardingProduct): string | null =>
  formatDayRange(shippingDayValues(product));

// Fastest to slowest delivery across the picked products, e.g. "1–7".
export const getDeliveryRange = (
  products: OnboardingProduct[]
): string | null => formatDayRange(products.flatMap(shippingDayValues));

export type ProductBadge = "bestseller" | "top_profit" | "trending";

// Badges for the review cards: Bestseller first, then the two highest
// profits, then products marked as trending.
export const getProductBadges = (
  products: OnboardingProduct[]
): Map<string, ProductBadge> => {
  const badges = new Map<string, ProductBadge>();
  products.forEach((product) => {
    if (product.best_selling) badges.set(product.id, "bestseller");
  });
  [...products]
    .filter((product) => !badges.has(product.id))
    .map((product) => ({ product, pricing: getProductPricing(product) }))
    .filter(({ pricing }) => pricing !== null)
    .sort((a, b) => b.pricing!.profitAmount - a.pricing!.profitAmount)
    .slice(0, 2)
    .forEach(({ product }) => badges.set(product.id, "top_profit"));
  products.forEach((product) => {
    if (product.trending && !badges.has(product.id)) {
      badges.set(product.id, "trending");
    }
  });
  return badges;
};
